import { useState } from 'react';
import { MdContentCopy } from 'react-icons/md';
import {
  Button,
  FormContextProvider,
  Icon,
  Input,
  InputField,
  Modal,
  ModalFooter,
} from '@nbirl/nbi-common-ui-libs';
import { Form } from 'formik';
import * as Yup from 'yup';

import { useCopyFolderContext } from '../../context';
import { Folder } from '../../models';
import { getCopyFolderName } from '../../utils';

const copyFolderValidationSchema = Yup.object().shape({
  name: Yup.string().required().max(100).label('Name'),
});

type CopyFolderFormValues = {
  name: string;
};

type CopyFolderModalProps = {
  isOpen: boolean;
  selectedFolder: Folder;
  currentFolders: Folder[];
  isSubFolder?: boolean;
  onCloseModal: () => void;
};

export const CopyFolderModal: React.FC<CopyFolderModalProps> = ({
  isOpen,
  isSubFolder,
  selectedFolder,
  currentFolders,
  onCloseModal,
}) => {
  const { copyFolder } = useCopyFolderContext();
  const [isMutationInProgress, setIsMutationInProgress] = useState(false);

  const handleOnCopy = async (values: CopyFolderFormValues) => {
    setIsMutationInProgress(true);

    try {
      await copyFolder(selectedFolder.id, values.name);
      onCloseModal();
    } catch {
      // error toast is displayed in CopyFolderProvider but error is re-thrown to enable
      // detecting the error scenario and not closing the copy folder modal
    } finally {
      setIsMutationInProgress(false);
    }
  };

  return (
    <Modal
      heading={isSubFolder ? 'Copy Folder' : 'Copy Main Folder'}
      isOpen={isOpen}
      headingIcon={<Icon icon={<MdContentCopy />} />}
      onDismiss={onCloseModal}
    >
      <FormContextProvider<CopyFolderFormValues>
        initialValues={{
          name: getCopyFolderName(currentFolders, selectedFolder.name),
        }}
        validateOnBlur={false}
        validationSchema={copyFolderValidationSchema}
        onSubmit={handleOnCopy}
      >
        {() => (
          <Form>
            <InputField name="name" showRequiredIndicator={false}>
              {(field) => <Input {...field} />}
            </InputField>

            <ModalFooter>
              <Button
                variant="outline"
                disabled={isMutationInProgress}
                onClick={onCloseModal}
              >
                Cancel
              </Button>
              <Button type="submit" isPending={isMutationInProgress}>
                Copy
              </Button>
            </ModalFooter>
          </Form>
        )}
      </FormContextProvider>
    </Modal>
  );
};
